import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { CONTACT } from "@/lib/content";

const LINKS = [
  { label: "Services", id: "services" },
  { label: "Why Us", id: "why" },
  { label: "Process", id: "process" },
  { label: "Industries", id: "industries" },
  { label: "About", id: "about" },
  { label: "Contact", id: "contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 transition-colors duration-500"
      style={{
        background: scrolled ? "rgba(255,255,255,0.85)" : "transparent",
        backdropFilter: scrolled ? "blur(16px)" : "none",
        borderBottom: scrolled ? "1px solid rgba(11,58,130,0.10)" : "1px solid transparent",
      }}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-12">
        <button
          data-testid="brand-logo"
          onClick={() => go("home")}
          className="flex items-center gap-3 text-left"
        >
          <span
            className="flex h-11 w-11 items-center justify-center overflow-hidden md:h-12 md:w-12"
            style={{
              background: scrolled ? "#fff" : "rgba(255,255,255,0.08)",
              border: scrolled ? "1px solid rgba(11,58,130,0.1)" : "1px solid rgba(255,255,255,0.15)",
            }}
          >
            <img
              src={CONTACT.logo}
              alt="Sanwariya Elevator Logo"
              className="h-full w-full object-contain p-1"
            />
          </span>
          <span
            className="font-display text-lg font-bold tracking-tight leading-none"
            style={{ color: scrolled ? "#0B3A82" : "#fff" }}
          >
            Sanwariya
            <span className="block text-[10px] font-body font-semibold uppercase tracking-[0.25em]" style={{ color: "#D89A18" }}>
              Elevator
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="font-body text-sm font-medium transition-colors hover:opacity-60"
              style={{ color: scrolled ? "#0B3A82" : "#fff" }}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-tollfree-btn"
            href={`tel:${CONTACT.tollFreeRaw}`}
            className="hidden flex-col items-end leading-tight md:flex"
          >
            <span
              className="font-body text-[10px] uppercase tracking-[0.22em]"
              style={{ color: scrolled ? "#475569" : "rgba(255,255,255,0.7)" }}
            >
              Toll Free
            </span>
            <span
              className="font-display text-sm font-bold tracking-tight"
              style={{ color: scrolled ? "#0B3A82" : "#fff" }}
            >
              {CONTACT.tollFree}
            </span>
          </a>
          <a
            data-testid="nav-call-btn"
            href={`tel:${CONTACT.phoneRaw}`}
            className="group hidden items-center gap-2 px-5 py-2.5 font-body text-sm font-semibold transition-all md:flex"
            style={{ background: "#D89A18", color: "#0B3A82" }}
          >
            <Phone size={15} />
            Call Now
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden"
            style={{ color: scrolled ? "#0B3A82" : "#fff" }}
            aria-label="Toggle menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden bg-white lg:hidden"
            style={{ borderTop: "1px solid rgba(11,58,130,0.10)" }}
          >
            <div className="flex flex-col px-6 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => go(l.id)}
                  className="border-b border-[#0B3A82]/10 py-3 text-left font-body font-medium text-[#0B3A82]"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                className="mt-4 flex items-center justify-center gap-2 py-3 font-body font-semibold"
                style={{ background: "#D89A18", color: "#0B3A82" }}
              >
                <Phone size={16} /> {CONTACT.phone}
              </a>
              <a
                href={`tel:${CONTACT.tollFreeRaw}`}
                className="mt-2 flex items-center justify-center gap-2 py-3 font-body font-semibold"
                style={{ background: "#0B3A82", color: "#fff" }}
              >
                <Phone size={16} /> Toll Free {CONTACT.tollFree}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
